import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import ChangeHistory, { KindToggles, isEntryVisible } from './ChangeHistory';

/**
 * Collapsed-by-default change log under the Services table: who changed which service's role fields
 * (Worship, Scripture Reading, Preaching...) in the month in view. Edits only by default; texts and replies can be
 * turned on. Follows the page's Services / Roles filter.
 */
export default function ServicesHistoryPanel({ month, monthLabel, activeServices, activeRoles, reloadKey }) {
	const [open, setOpen] = useState(false);
	const [kinds, setKinds] = useState(['edit']);
	const [entries, setEntries] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const loadedFor = useRef(null);
	const request = useRef(0);
	const navigate = useNavigate();

	const load = useCallback(async () => {
		const id = ++request.current;
		setLoading(true);
		setError(null);
		// another month: don't show the previous month's changes while loading
		if (!loadedFor.current?.startsWith(`${month}|`)) setEntries([]);
		try {
			const data = await api.getServicesHistory(month);
			if (id !== request.current) return;
			setEntries(data.entries ?? []);
			loadedFor.current = `${month}|${reloadKey}`;
		}
		catch (ex) {
			if (id === request.current) setError(ex);
		}
		finally {
			if (id === request.current) setLoading(false);
		}
	}, [month, reloadKey]);

	// load when first opened, and again when the month changes or after a save while it is open
	useEffect(() => {
		if (open && loadedFor.current !== `${month}|${reloadKey}`) load();
	}, [open, month, reloadKey, load]);

	const filtered = entries
		.filter(e => !activeServices || activeServices.includes(e.templateId))
		.map(e => ({ ...e, changes: (e.changes ?? []).filter(c => !activeRoles || (c.roleIds ?? []).some(r => activeRoles.includes(r))) }))
		.filter(e => e.action !== 'update' || e.changes.length > 0);

	const counts = {};
	['edit', 'notification', 'confirmation'].forEach(k => { counts[k] = filtered.filter(e => isEntryVisible(e, [k])).length; });

	return (
		<div className='ch-panel'>
			<button type='button' className='ch-panel-head' aria-expanded={open} onClick={() => setOpen(!open)}>
				<Icon name={open ? 'chevron down' : 'chevron right'} />
				Change history
				<span className='ch-panel-sub'>Who changed {monthLabel} services, and when</span>
			</button>
			{open &&
				<div className='ch-panel-body'>
					<div className='ch-panel-tools'>
						<KindToggles kinds={kinds} onChange={setKinds} counts={loading ? undefined : counts} />
						<Button basic compact size='small' icon='refresh' content='Refresh' onClick={load} disabled={loading} />
					</div>
					<ChangeHistory
						entries={filtered}
						kinds={kinds}
						loading={loading && entries.length === 0}
						error={error}
						showTarget
						useRoles
						onOpenTarget={(e) => navigate(`/editor?id=${e.docId}&tab=History`)}
						emptyText={kinds.length === 0 ? 'Turn on Edits, Notifications or Replies to see changes.' : `No ${kinds.length === 1 && kinds[0] === 'edit' ? 'edits' : 'changes'} to ${monthLabel} services yet.`}
					/>
				</div>}
		</div>
	);
}
